/**
 * URLの正本。画面の識別子（workspace/app/run）と選択状態をURLへ入れ、
 * 直接アクセスと戻る/進むで同じ対象を復元する。リンク生成もここへ集約する。
 * runは必ずappの下に置き、別appのrun IDを混ぜたURLを作れないようにする。
 */

export const APP_TABS = ['client', 'process', 'decisions', 'code', 'config', 'connections', 'runs', 'review'] as const;
export type AppTab = (typeof APP_TABS)[number];

/** 要素の詳細パネル内のタブ。出典表示を回答待ちに限定しないため常に選べる。 */
export const DETAIL_PANELS = ['detail', 'answer', 'source'] as const;
export type DetailPanel = (typeof DETAIL_PANELS)[number];

export const DMN_VIEWS = ['table', 'drd'] as const;
export type DmnView = (typeof DMN_VIEWS)[number];

/** コードタブの版の差分の視点。file が既定。 */
export const DRAFT_DIFF_VIEWS = ['file', 'element', 'source'] as const;
export type DraftDiffView = (typeof DRAFT_DIFF_VIEWS)[number];

/** Studio のキャンバスの表示。memo（整理メモ）が既定。 */
export const STUDIO_VIEWS = ['memo', 'process', 'decisions', 'code', 'review'] as const;
export type StudioView = (typeof STUDIO_VIEWS)[number];

/** 知識ソース画面の表示。files が既定。 */
export const KNOWLEDGE_TABS = ['files', 'summary', 'memories'] as const;
export type KnowledgeTab = (typeof KNOWLEDGE_TABS)[number];

export interface WorkspacesRoute {
  name: 'workspaces';
  q?: string;
}

export interface WorkspaceRoute {
  name: 'workspace';
  workspaceId: string;
  /** 登録進捗を追うためのupload。 */
  uploadId?: string;
}

export interface KnowledgeRoute {
  name: 'knowledge';
  workspaceId: string;
  /** ファイル名の検索語。 */
  q?: string;
  /** 会話まとめ・記憶のタブ。'files' は既定なので持たない。 */
  tab?: 'summary' | 'memories';
}

export interface AppRoute {
  name: 'app';
  workspaceId: string;
  appId: string;
  tab: AppTab;
  /** processタブで表示中のBPMN。 */
  process?: string;
  /** 選択中のBPMN要素ID。 */
  element?: string;
  panel?: DetailPanel;
  /** decisionsタブで表示中のDMN。 */
  decision?: string;
  view?: DmnView;
  /** codeタブで表示中のファイル。ZIP内のパスをそのまま入れる。 */
  file?: string;
  /** codeタブで Draft の版の履歴を開いているとき 'history'。 */
  codePanel?: 'history';
  /** 版の履歴で差分を見ている版。 */
  revision?: string;
  /** 差分の視点。'file' は既定なので URL に出さない。 */
  diffView?: DraftDiffView;
}

export interface RunRoute {
  name: 'run';
  workspaceId: string;
  appId: string;
  runId: string;
  element?: string;
  /** ループ/MIで同じactivityIdの実行回を区別する。 */
  instance?: string;
  panel?: DetailPanel;
}

export interface StudioRoute {
  name: 'studio';
  workspaceId: string;
  /** 成果物のタブで選んだ microapp（URL app）。 */
  appId?: string;
  /** 'memo' は既定なので URL に出さない。 */
  view?: StudioView;
  /** 表示中のファイル（Draft の論理パス）。 */
  file?: string;
  /** 業務フローで選んだ要素。 */
  element?: string;
}

export interface NotFoundRoute {
  name: 'notFound';
  path: string;
}

export type Route =
  | WorkspacesRoute
  | WorkspaceRoute
  | KnowledgeRoute
  | StudioRoute
  | AppRoute
  | RunRoute
  | NotFoundRoute;

export function href(route: Route): string {
  switch (route.name) {
    case 'workspaces':
      return withQuery('/', { q: route.q });
    case 'workspace':
      return withQuery(`/w/${seg(route.workspaceId)}`, { upload: route.uploadId });
    case 'knowledge':
      return withQuery(`/w/${seg(route.workspaceId)}/knowledge`, { q: route.q, tab: route.tab });
    case 'studio':
      return withQuery(`/w/${seg(route.workspaceId)}/studio`, {
        app: route.appId,
        view: route.view === 'memo' ? undefined : route.view,
        file: route.file,
        element: route.element,
      });
    case 'app':
      return withQuery(`/w/${seg(route.workspaceId)}/apps/${seg(route.appId)}`, {
        tab: route.tab === 'process' ? undefined : route.tab,
        process: route.process,
        element: route.element,
        panel: route.panel === 'detail' ? undefined : route.panel,
        decision: route.decision,
        view: route.view === 'table' ? undefined : route.view,
        file: route.file,
        codePanel: route.codePanel,
        rev: route.revision,
        diff: route.diffView === 'file' ? undefined : route.diffView,
      });
    case 'run':
      return withQuery(
        `/w/${seg(route.workspaceId)}/apps/${seg(route.appId)}/runs/${seg(route.runId)}`,
        {
          element: route.element,
          instance: route.instance,
          panel: route.panel === 'detail' ? undefined : route.panel,
        },
      );
    case 'notFound':
      return route.path;
  }
}

export function parseRoute(url: string): Route {
  const [rawPath = '', rawQuery = ''] = url.split('?');
  const query = new URLSearchParams(rawQuery);
  const parts = rawPath.split('/').filter((part) => part !== '');

  if (parts.length === 0) {
    return { name: 'workspaces', ...optional('q', query.get('q')) };
  }
  if (parts[0] !== 'w' || parts.length < 2) return { name: 'notFound', path: url };

  const workspaceId = decode(parts[1] ?? '');
  if (parts.length === 2) {
    return { name: 'workspace', workspaceId, ...optional('uploadId', query.get('upload')) };
  }
  if (parts[2] === 'knowledge') {
    return parts.length === 3
      ? { name: 'knowledge', workspaceId, ...optional('q', query.get('q')), ...knowledgeTabOf(query.get('tab')) }
      : { name: 'notFound', path: url };
  }
  if (parts[2] === 'studio') {
    if (parts.length !== 3) return { name: 'notFound', path: url };
    return {
      name: 'studio',
      workspaceId,
      ...optional('appId', query.get('app')),
      ...studioViewOf(query.get('view')),
      ...optional('file', query.get('file')),
      ...optional('element', query.get('element')),
    };
  }
  if (parts[2] !== 'apps' || parts.length < 4) return { name: 'notFound', path: url };

  const appId = decode(parts[3] ?? '');
  if (parts.length === 4) {
    return {
      name: 'app',
      workspaceId,
      appId,
      tab: pick(APP_TABS, query.get('tab'), 'process'),
      ...optional('process', query.get('process')),
      ...optional('element', query.get('element')),
      panel: pick(DETAIL_PANELS, query.get('panel'), 'detail'),
      ...optional('decision', query.get('decision')),
      view: pick(DMN_VIEWS, query.get('view'), 'table'),
      ...optional('file', query.get('file')),
      ...(query.get('codePanel') === 'history' ? { codePanel: 'history' as const } : {}),
      ...optional('revision', query.get('rev')),
      ...diffViewOf(query.get('diff')),
    };
  }
  if (parts[4] !== 'runs') return { name: 'notFound', path: url };
  if (parts.length === 5) {
    // 実行履歴一覧はapp詳細のタブとして表示する。独立した一覧ページは作らない。
    return { name: 'app', workspaceId, appId, tab: 'runs', panel: 'detail', view: 'table' };
  }
  if (parts.length !== 6) return { name: 'notFound', path: url };

  return {
    name: 'run',
    workspaceId,
    appId,
    runId: decode(parts[5] ?? ''),
    ...optional('element', query.get('element')),
    ...optional('instance', query.get('instance')),
    panel: pick(DETAIL_PANELS, query.get('panel'), 'detail'),
  };
}

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, value);
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

function seg(value: string): string {
  return encodeURIComponent(value);
}

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function pick<T extends readonly string[]>(
  allowed: T,
  value: string | null,
  fallback: T[number],
): T[number] {
  return value !== null && (allowed as readonly string[]).includes(value)
    ? (value as T[number])
    : fallback;
}

function optional<K extends string>(key: K, value: string | null): Partial<Record<K, string>> {
  return value ? ({ [key]: value } as Record<K, string>) : {};
}

/** Studio の表示。既定の memo は URL に載せないので、読むのはほかの 4 つだけ。 */
function studioViewOf(value: string | null): { view?: StudioView } {
  return value === 'process' || value === 'decisions' || value === 'code' || value === 'review' ? { view: value } : {};
}

/** 知識ソース画面のタブ。既定の files は URL に載せないので、読むのは summary と memories だけ。 */
function knowledgeTabOf(value: string | null): { tab?: 'summary' | 'memories' } {
  return value === 'summary' || value === 'memories' ? { tab: value } : {};
}

/** 差分の視点。既定の file は URL に載せないので、読むのは element と source だけ。 */
function diffViewOf(value: string | null): { diffView?: DraftDiffView } {
  return value === 'element' || value === 'source' ? { diffView: value } : {};
}
